import React, { useState, useMemo } from 'react'
import {
  Search,
  CheckCircle2,
  AlertTriangle,
  FileText,
  Ship,
  RefreshCw,
  Calculator,
  Building2,
  Package,
  Boxes,
  FileCheck
} from 'lucide-react'
import toast from 'react-hot-toast'
import { ExpedienteDespacho } from '../PaginaDespachoPortuario'

interface Props {
  expediente?: ExpedienteDespacho | null
  todosExpedientes: ExpedienteDespacho[]
  onSeleccionarExpediente: (exp: ExpedienteDespacho) => void
  onAbrirHojaFisica: (tipoDoc: 'PEDIMENTO' | 'BL' | 'TARJA' | 'FACTURA') => void
  onActualizarExpediente: (expActualizado: ExpedienteDespacho) => void
}

export const MesaPrevioAduanero: React.FC<Props> = ({
  expediente,
  todosExpedientes,
  onSeleccionarExpediente,
  onAbrirHojaFisica,
  onActualizarExpediente
}) => {
  // --- 1. Estado de Revalidación Naviera (Pizarrón: Persona Física -> Naviera -> A.A. -> B/L M / B/L H -> Revalidación) ---
  const [tipoBL, setTipoBL] = useState<'BL_MASTER' | 'BL_HOUSE'>('BL_MASTER')
  const [revalidado, setRevalidado] = useState<boolean>(true)
  const [costoRevalidacionUSD] = useState<number>(20.0) // Como anotó el profesor en la esquina superior izquierda ($20.00)

  // --- 2. Estado de Terminal y Maniobras (Pizarrón: Almacenes SSA, Timsa, Contecon, Ocupa, TAP / Contenedor vs Carga Suelta / Maniobras P, B) ---
  const [terminalSeleccionada, setTerminalSeleccionada] = useState<'SSA' | 'TIMSA' | 'CONTECON' | 'OCUPA' | 'TAP'>('SSA')
  const [tipoCarga, setTipoCarga] = useState<'CONTENEDOR_FCL' | 'CARGA_SUELTA_LCL'>('CONTENEDOR_FCL')
  const [maniobraPrevioP, setManiobraPrevioP] = useState<boolean>(true)
  const [maniobraBasculaB, setManiobraBasculaB] = useState<boolean>(true)

  // --- 3. Estado del Previo: Documental vs Físico (Pizarrón: Factura $1,000.00 vs Físico -> NO = Igual) ---
  const [documentalValorUSD, setDocumentalValorUSD] = useState<number>(expediente?.documentos?.factura?.valorUSD ?? 1000)
  const [documentalBultos, setDocumentalBultos] = useState<number>(expediente?.documentos?.packingList?.bultos ?? 1000)
  const [documentalPesoKg, setDocumentalPesoKg] = useState<number>(expediente?.contenedor?.pesoBrutoKg ?? 24850)
  const [documentalSello, setDocumentalSello] = useState<string>(expediente?.contenedor?.sellos ?? 'MX-SAT-992144')

  // Datos Físicos en Muelle / Recinto
  const [fisicoBultos, setFisicoBultos] = useState<number>(expediente?.documentos?.packingList?.bultos ?? 1000)
  const [fisicoPesoKg, setFisicoPesoKg] = useState<number>(expediente?.contenedor?.pesoBrutoKg ?? 24850)
  const [fisicoSellosCoinciden, setFisicoSellosCoinciden] = useState<boolean>(true)
  const [fisicoEtiquetadoNOM, setFisicoEtiquetadoNOM] = useState<boolean>(true)
  const [observacionPrevio, setObservacionPrevio] = useState<string>('')

  // --- 4. Estado de Clasificación y Calculadora Arancelaria (Pizarrón: Pedimento = Fracción, RRNAs, IGI: Ad-valorem %, Específico $, Mixto) ---
  const [fraccionArancelaria, setFraccionArancelaria] = useState<string>(
    expediente?.regimen === 'IR_RFE' ? '0804.40.01.00' : '8471.30.01.00'
  )
  const [tipoArancel, setTipoArancel] = useState<'AD_VALOREM' | 'ESPECIFICO' | 'MIXTO'>('AD_VALOREM')
  const [tasaAdValorem, setTasaAdValorem] = useState<number>(15) // 15%
  const [cuotaEspecificaUSD, setCuotaEspecificaUSD] = useState<number>(0.36) // Como en la pizarra: $0.36
  const [cumpleRRNAs, setCumpleRRNAs] = useState<boolean>(true)

  // Comparación Documental vs Físico (Ecuación del Pizarrón)
  const sonIguales = useMemo(() => {
    const bultosIguales = Number(documentalBultos) === Number(fisicoBultos)
    const pesoTolerancia = Math.abs(Number(documentalPesoKg) - Number(fisicoPesoKg)) <= 500 // Tolerancia SOLAS
    return bultosIguales && pesoTolerancia && fisicoSellosCoinciden && fisicoEtiquetadoNOM
  }, [documentalBultos, fisicoBultos, documentalPesoKg, fisicoPesoKg, fisicoSellosCoinciden, fisicoEtiquetadoNOM])

  // Cálculo de Impuestos en vivo
  const calculosIGI = useMemo(() => {
    const valorAduanaUSD = documentalValorUSD
    let igiUSD = 0

    if (tipoArancel === 'AD_VALOREM') {
      igiUSD = valorAduanaUSD * (tasaAdValorem / 100)
    } else if (tipoArancel === 'ESPECIFICO') {
      // Cuota por bulto/unidad o kg
      igiUSD = (documentalPesoKg / 100) * cuotaEspecificaUSD
    } else {
      // Mixto: Ad-valorem + Específico
      igiUSD = valorAduanaUSD * (tasaAdValorem / 100) + (documentalPesoKg / 100) * cuotaEspecificaUSD
    }

    const dtaUSD = 25
    const prvUSD = 15
    const ivaUSD = (valorAduanaUSD + igiUSD + dtaUSD) * 0.16
    const totalUSD = igiUSD + dtaUSD + prvUSD + ivaUSD

    return {
      valorAduanaUSD,
      igiUSD: Math.round(igiUSD),
      dtaUSD,
      prvUSD,
      ivaUSD: Math.round(ivaUSD),
      totalUSD: Math.round(totalUSD)
    }
  }, [documentalValorUSD, documentalPesoKg, tipoArancel, tasaAdValorem, cuotaEspecificaUSD])

  // Inyectar Discrepancia para Evaluación (Herramienta del Maestro)
  const handleInyectarDiscrepancia = () => {
    setFisicoBultos(documentalBultos - 120) // Faltan 120 bultos
    setFisicoSellosCoinciden(false)
    toast.error('Discrepancia inyectada en muelle: Bultos físicos (880) != Factura (1000) y sellos violentados.')
  }

  // Restablecer Conforme
  const handleRestablecerConforme = () => {
    setFisicoBultos(documentalBultos)
    setFisicoPesoKg(documentalPesoKg)
    setFisicoSellosCoinciden(true)
    setFisicoEtiquetadoNOM(true)
    toast.success('Valores físicos sincronizados con la documentación. Previo conforme.')
  }

  // Finalizar Previo y Aprobar para Pedimento
  const handleAprobarPrevio = () => {
    if (!expediente) return
    if (!sonIguales) {
      toast.error('No se puede aprobar el previo con discrepancias activas. Levanta acta de incidencia primero.')
      return
    }

    const expActualizado: ExpedienteDespacho = {
      ...expediente,
      estado: 'ESPERANDO_MODULACION',
      documentos: {
        ...expediente.documentos,
        factura: {
          ...expediente.documentos.factura,
          valorUSD: documentalValorUSD
        }
      }
    }

    onActualizarExpediente(expActualizado)
    toast.success('¡Previo Aprobado! Documental y Físico coinciden. Expediente listo para el Pedimento Oficial.')
  }

  // Levantar Acta de Discrepancia (Evitar PAMA en Aduana)
  const handleLevantarDiscrepancia = () => {
    if (!expediente) return
    const expActualizado: ExpedienteDespacho = {
      ...expediente,
      estado: 'INCIDENCIA',
      incidencia: {
        activa: true,
        codigo: 'DISCREPANCIA_PREVIO_ADUANAL',
        descripcion:
          observacionPrevio ||
          `Discrepancia física detectada en el examen previo: Documental (${documentalBultos} bultos) != Físico (${fisicoBultos} bultos). Sello no legible.`,
        solucion:
          'Notificar inmediatamente a la Agencia Aduanal y al exportador para emitir carta aclaratoria y rectificar factura antes del pedimento.'
      }
    }

    onActualizarExpediente(expActualizado)
    toast.error('Acta de Hechos en Previo levantada. Se detuvo la modulación para evitar embargo o PAMA.')
  }

  if (!expediente) {
    return (
      <div className="p-10 rounded-[24px] bg-white/80 dark:bg-[#0c0c0c] border border-slate-200/80 dark:border-white/[0.08] backdrop-blur-xl shadow-xl text-center max-w-xl mx-auto my-8 space-y-4">
        <div className="w-16 h-16 rounded-2xl bg-blue-500/10 border border-blue-500/20 text-blue-500 flex items-center justify-center mx-auto">
          <Search className="w-8 h-8" />
        </div>
        <h3 className="text-xl font-bold text-slate-900 dark:text-white">
          Mesa de Previo Aduanero
        </h3>
        <p className="text-sm text-slate-600 dark:text-zinc-400">
          No hay un expediente seleccionado para inspeccionar en la mesa de previo. Selecciona un salón con expedientes o genera una práctica con células para 4-CAS-1.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Encabezado Pedagógico del Previo (Basado en la Pizarra) */}
      <div className="p-6 sm:p-7 rounded-3xl bg-gradient-to-r from-blue-950/40 via-indigo-950/20 to-slate-900 border border-blue-500/30 shadow-xl">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
          <div className="flex items-start gap-4">
            <div className="p-3.5 rounded-2xl bg-blue-600 text-white shadow-lg shadow-blue-500/30">
              <Search className="h-7 w-7" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-black uppercase tracking-widest px-2.5 py-0.5 rounded-full bg-blue-500/20 text-blue-400 border border-blue-500/30">
                  Flujo Oficial Manzanillo
                </span>
                <span className="text-xs font-mono font-bold text-slate-400">
                  Expediente: {expediente.folio}
                </span>
              </div>
              <h2 className="text-xl sm:text-2xl font-black text-slate-900 dark:text-white mt-1">
                Mesa del Previo Aduanero: Revalidación, Maniobras y Examen Ocular
              </h2>
              <p className="mt-1 text-xs text-slate-600 dark:text-zinc-400 max-w-3xl leading-relaxed">
                El <strong>Examen Previo (Art. 42 Ley Aduanera)</strong> es la inspección ocular y conteo que realiza el <strong>Agente Aduanal</strong> en el recinto fiscalizado. Aquí se comprueba la regla de oro: si <strong>Documental != Físico</strong>, se levanta acta de hechos para evitar multas, subvaluación o un <strong>PAMA (Procedimiento Administrativo en Materia Aduanera)</strong>.
              </p>
            </div>
          </div>

          {/* Selector de Expediente Rápido */}
          <div className="flex flex-col sm:flex-row items-end gap-3 shrink-0">
            <div className="text-right">
              <span className="text-[10px] font-bold uppercase text-slate-400 block">Expediente en Mesa:</span>
              <select
                value={expediente.id}
                onChange={(e) => {
                  const enc = todosExpedientes.find((x) => x.id === e.target.value)
                  if (enc) onSeleccionarExpediente(enc)
                }}
                className="mt-1 px-3 py-1.5 rounded-xl bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 text-xs font-bold text-slate-900 dark:text-white"
              >
                {todosExpedientes.map((x) => (
                  <option key={x.id} value={x.id} className="dark:bg-slate-900">
                    {x.folio} - {x.contenedor.numero}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Grid de 3 Columnas: 1. Revalidación & Terminal -> 2. El Previo (Documental vs Físico) -> 3. Impuestos IGI */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* ========================================================================= */}
        {/* COLUMNA 1 (4 COLS): REVALIDACIÓN NAVIERA Y TERMINALES DE MANZANILLO */}
        {/* ========================================================================= */}
        <div className="lg:col-span-4 space-y-5">
          {/* Tarjeta A: Revalidación Naviera (BL Master vs House) */}
          <div className="p-5 rounded-2xl border border-slate-200/80 bg-white dark:border-white/10 dark:bg-[#0c0c0e] shadow-sm space-y-4">
            <div className="flex items-center justify-between pb-3 border-b border-slate-100 dark:border-white/5">
              <h3 className="text-xs font-black uppercase tracking-wider text-slate-900 dark:text-white flex items-center gap-2">
                <Ship className="h-4 w-4 text-cyan-500" />
                1. Revalidación de B/L ante Naviera
              </h3>
              <span className="font-mono text-xs font-bold text-emerald-500 bg-emerald-500/10 px-2 py-0.5 rounded">
                ${costoRevalidacionUSD.toFixed(2)} USD
              </span>
            </div>

            <p className="text-[11px] text-slate-600 dark:text-zinc-400">
              Trámite ante la naviera para canjear el documento de transporte por la autorización de entrega portuaria.
            </p>

            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setTipoBL('BL_MASTER')}
                className={`p-2.5 rounded-xl border text-center transition-all ${
                  tipoBL === 'BL_MASTER'
                    ? 'border-cyan-500 bg-cyan-500/10 text-cyan-400 font-bold shadow-sm'
                    : 'border-slate-200 dark:border-white/10 text-slate-600 dark:text-zinc-400'
                }`}
              >
                <span className="block text-xs font-black">B/L Master (BL M)</span>
                <span className="text-[9px] opacity-75">Naviera Directa</span>
              </button>

              <button
                type="button"
                onClick={() => setTipoBL('BL_HOUSE')}
                className={`p-2.5 rounded-xl border text-center transition-all ${
                  tipoBL === 'BL_HOUSE'
                    ? 'border-cyan-500 bg-cyan-500/10 text-cyan-400 font-bold shadow-sm'
                    : 'border-slate-200 dark:border-white/10 text-slate-600 dark:text-zinc-400'
                }`}
              >
                <span className="block text-xs font-black">B/L House (BL H)</span>
                <span className="text-[9px] opacity-75">Freight Forwarder</span>
              </button>
            </div>

            <div className="flex items-center justify-between p-3 rounded-xl bg-slate-50 dark:bg-white/[0.02] border border-slate-200 dark:border-white/10">
              <div>
                <span className="text-[10px] font-bold uppercase text-slate-400 block">Estatus de Revalidación:</span>
                <span className="font-black text-xs text-emerald-600 dark:text-emerald-400">
                  {revalidado ? '✓ REVALIDADO / LIBERADO' : '✗ PENDIENTE DE PAGO'}
                </span>
              </div>
              <button
                type="button"
                onClick={() => {
                  setRevalidado(!revalidado)
                  toast.success(revalidado ? 'Estatus marcado como pendiente' : '¡B/L Revalidado exitosamente!')
                }}
                className="px-3 py-1.5 rounded-lg border border-slate-300 dark:border-white/10 text-[10px] font-bold hover:bg-white/5 transition-all text-slate-700 dark:text-zinc-300"
              >
                {revalidado ? 'Cancelar' : 'Revalidar'}
              </button>
            </div>

            <button
              type="button"
              onClick={() => onAbrirHojaFisica('BL')}
              className="w-full py-2 rounded-xl border border-cyan-500/30 bg-cyan-500/10 text-cyan-600 dark:text-cyan-400 text-xs font-bold hover:bg-cyan-500/20 transition-all flex items-center justify-center gap-1.5"
            >
              <FileText className="h-3.5 w-3.5" />
              <span>Ver Hoja Física del B/L Marítimo</span>
            </button>
          </div>

          {/* Tarjeta B: Terminales Concesionadas de Manzanillo (SSA, TIMSA, Contecon, Ocupa, TAP) */}
          <div className="p-5 rounded-2xl border border-slate-200/80 bg-white dark:border-white/10 dark:bg-[#0c0c0e] shadow-sm space-y-4">
            <div className="flex items-center justify-between pb-3 border-b border-slate-100 dark:border-white/5">
              <h3 className="text-xs font-black uppercase tracking-wider text-slate-900 dark:text-white flex items-center gap-2">
                <Building2 className="h-4 w-4 text-blue-500" />
                2. Almacén / Terminal en Manzanillo
              </h3>
              <span className="text-[10px] font-bold text-slate-400">ASIPONA</span>
            </div>

            <div className="grid grid-cols-5 gap-1.5 font-mono">
              {[
                { id: 'SSA', label: 'SSA', sub: 'TEC I' },
                { id: 'TIMSA', label: 'TIMSA', sub: 'Hutchison' },
                { id: 'CONTECON', label: 'CONTECON', sub: 'TEC II' },
                { id: 'OCUPA', label: 'OCUPA', sub: 'Muelle' },
                { id: 'TAP', label: 'TAP', sub: 'Patio' }
              ].map((term) => (
                <button
                  key={term.id}
                  type="button"
                  onClick={() => setTerminalSeleccionada(term.id as any)}
                  className={`p-2 rounded-xl border text-center transition-all ${
                    terminalSeleccionada === term.id
                      ? 'border-blue-500 bg-blue-600 text-white font-black shadow-md shadow-blue-500/25 scale-105'
                      : 'border-slate-200 dark:border-white/10 text-slate-700 dark:text-zinc-400 hover:bg-slate-100 dark:hover:bg-white/5'
                  }`}
                >
                  <span className="block text-xs font-black">{term.label}</span>
                  <span className="block text-[8px] opacity-75">{term.sub}</span>
                </button>
              ))}
            </div>

            {/* Modalidad de Carga: Contenedor vs Carga Suelta */}
            <div className="pt-2 border-t border-slate-100 dark:border-white/5 space-y-2">
              <span className="text-[10px] font-bold uppercase text-slate-400 block">Tipo de Carga en Puerto:</span>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setTipoCarga('CONTENEDOR_FCL')}
                  className={`p-2 rounded-lg border text-xs font-bold transition-all flex items-center justify-center gap-1.5 ${
                    tipoCarga === 'CONTENEDOR_FCL'
                      ? 'border-blue-500 bg-blue-500/10 text-blue-600 dark:text-blue-400'
                      : 'border-slate-200 dark:border-white/10 text-slate-600 dark:text-zinc-400'
                  }`}
                >
                  <Boxes className="h-3.5 w-3.5" />
                  <span>Contenedor (FCL)</span>
                </button>

                <button
                  type="button"
                  onClick={() => setTipoCarga('CARGA_SUELTA_LCL')}
                  className={`p-2 rounded-lg border text-xs font-bold transition-all flex items-center justify-center gap-1.5 ${
                    tipoCarga === 'CARGA_SUELTA_LCL'
                      ? 'border-blue-500 bg-blue-500/10 text-blue-600 dark:text-blue-400'
                      : 'border-slate-200 dark:border-white/10 text-slate-600 dark:text-zinc-400'
                  }`}
                >
                  <Package className="h-3.5 w-3.5" />
                  <span>Carga Suelta (LCL)</span>
                </button>
              </div>
            </div>

            {/* Maniobras Solicitadas: P y B (Pizarrón) */}
            <div className="pt-2 border-t border-slate-100 dark:border-white/5 space-y-2">
              <span className="text-[10px] font-bold uppercase text-slate-400 block">Maniobras en Recinto:</span>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <label className="flex items-center gap-2 p-2 rounded-lg border border-slate-200 dark:border-white/10 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={maniobraPrevioP}
                    onChange={(e) => setManiobraPrevioP(e.target.checked)}
                    className="rounded text-blue-600"
                  />
                  <span className="font-bold text-slate-800 dark:text-zinc-200">Maniobra Previo (P)</span>
                </label>

                <label className="flex items-center gap-2 p-2 rounded-lg border border-slate-200 dark:border-white/10 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={maniobraBasculaB}
                    onChange={(e) => setManiobraBasculaB(e.target.checked)}
                    className="rounded text-blue-600"
                  />
                  <span className="font-bold text-slate-800 dark:text-zinc-200">Báscula Plataforma (B)</span>
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* ========================================================================= */}
        {/* COLUMNA 2 (5 COLS): MESA DE REVISIÓN PREVIA (DOCUMENTAL VS FÍSICO) */}
        {/* ========================================================================= */}
        <div className="lg:col-span-5 space-y-5">
          <div className="p-5 rounded-2xl border border-slate-200/80 bg-white dark:border-white/10 dark:bg-[#0c0c0e] shadow-sm space-y-4">
            <div className="flex items-center justify-between pb-3 border-b border-slate-100 dark:border-white/5">
              <div>
                <h3 className="text-xs font-black uppercase tracking-wider text-slate-900 dark:text-white flex items-center gap-2">
                  <Search className="h-4 w-4 text-purple-500" />
                  3. Examen Previo: Documental vs Físico
                </h3>
                <p className="text-[10px] text-slate-500 font-mono mt-0.5">Art. 42 Ley Aduanera</p>
              </div>

              {/* Resultado del Previo: Conforme vs Discrepancia */}
              <span
                className={`px-3 py-1 rounded-full text-xs font-black uppercase tracking-wider border flex items-center gap-1 ${
                  sonIguales
                    ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/30'
                    : 'bg-rose-500/10 text-rose-600 dark:text-rose-400 border-rose-500/30 animate-pulse'
                }`}
              >
                {sonIguales ? '✓ DOCUMENTAL == FÍSICO' : '✗ DOCUMENTAL != FÍSICO'}
              </span>
            </div>

            {/* Matriz de Cotejo Comparativa */}
            <div className="grid grid-cols-2 gap-3">
              {/* Panel Izquierdo: Documental */}
              <div className="p-3.5 rounded-xl border border-blue-500/30 bg-blue-50/50 dark:bg-blue-950/20 space-y-2.5">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-black uppercase text-blue-700 dark:text-blue-300">
                    A. Documental (Factura / BL)
                  </span>
                  <button
                    type="button"
                    onClick={() => onAbrirHojaFisica('FACTURA')}
                    className="text-[9px] font-bold text-blue-600 hover:underline"
                  >
                    Ver Factura
                  </button>
                </div>

                <div>
                  <label className="block text-[9px] font-bold text-slate-600 dark:text-zinc-400 uppercase">
                    Valor Declarado (Factura / $)
                  </label>
                  <div className="relative mt-0.5">
                    <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-slate-400 font-bold">$</span>
                    <input
                      type="number"
                      value={documentalValorUSD}
                      onChange={(e) => setDocumentalValorUSD(Number(e.target.value))}
                      className="w-full pl-6 pr-2 py-1.5 rounded-lg text-xs font-mono font-bold bg-white dark:bg-white/5 border border-slate-300 dark:border-white/10 dark:text-white"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-[9px] font-bold text-slate-600 dark:text-zinc-400 uppercase">
                    Bultos Declarados
                  </label>
                  <input
                    type="number"
                    value={documentalBultos}
                    onChange={(e) => setDocumentalBultos(Number(e.target.value))}
                    className="w-full px-2.5 py-1.5 rounded-lg text-xs font-mono font-bold bg-white dark:bg-white/5 border border-slate-300 dark:border-white/10 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-[9px] font-bold text-slate-600 dark:text-zinc-400 uppercase">
                    Peso Declarado (Kg)
                  </label>
                  <input
                    type="number"
                    value={documentalPesoKg}
                    onChange={(e) => setDocumentalPesoKg(Number(e.target.value))}
                    className="w-full px-2.5 py-1.5 rounded-lg text-xs font-mono font-bold bg-white dark:bg-white/5 border border-slate-300 dark:border-white/10 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-[9px] font-bold text-slate-600 dark:text-zinc-400 uppercase">
                    Sello / Precinto Declarado
                  </label>
                  <input
                    type="text"
                    value={documentalSello}
                    onChange={(e) => setDocumentalSello(e.target.value)}
                    className="w-full px-2.5 py-1.5 rounded-lg text-xs font-mono font-bold bg-white dark:bg-white/5 border border-slate-300 dark:border-white/10 dark:text-white"
                  />
                </div>
              </div>

              {/* Panel Derecho: Físico en Muelle / Recinto */}
              <div className="p-3.5 rounded-xl border border-purple-500/30 bg-purple-50/50 dark:bg-purple-950/20 space-y-2.5">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-black uppercase text-purple-700 dark:text-purple-300">
                    B. Físico (Tarja en Muelle)
                  </span>
                  <button
                    type="button"
                    onClick={() => onAbrirHojaFisica('TARJA')}
                    className="text-[9px] font-bold text-purple-600 hover:underline"
                  >
                    Ver Tarja
                  </button>
                </div>

                <div>
                  <label className="block text-[9px] font-bold text-slate-600 dark:text-zinc-400 uppercase">
                    Inspección Ocular / Estado
                  </label>
                  <div className="p-1.5 rounded-lg bg-white/70 dark:bg-white/5 border border-slate-200 dark:border-white/10 text-[10px] text-slate-700 dark:text-zinc-300">
                    Contenedor abierto en punto de previo. Sellos verificados.
                  </div>
                </div>

                <div>
                  <label className="block text-[9px] font-bold text-slate-600 dark:text-zinc-400 uppercase">
                    Bultos Contados Físicamente
                  </label>
                  <input
                    type="number"
                    value={fisicoBultos}
                    onChange={(e) => setFisicoBultos(Number(e.target.value))}
                    className={`w-full px-2.5 py-1.5 rounded-lg text-xs font-mono font-bold bg-white dark:bg-white/5 border ${
                      fisicoBultos !== documentalBultos
                        ? 'border-rose-500 text-rose-600 dark:text-rose-400'
                        : 'border-slate-300 dark:border-white/10 dark:text-white'
                    }`}
                  />
                </div>

                <div>
                  <label className="block text-[9px] font-bold text-slate-600 dark:text-zinc-400 uppercase">
                    Peso Báscula Real (Kg)
                  </label>
                  <input
                    type="number"
                    value={fisicoPesoKg}
                    onChange={(e) => setFisicoPesoKg(Number(e.target.value))}
                    className="w-full px-2.5 py-1.5 rounded-lg text-xs font-mono font-bold bg-white dark:bg-white/5 border border-slate-300 dark:border-white/10 dark:text-white"
                  />
                </div>

                <div className="space-y-1.5 pt-1">
                  <label className="flex items-center gap-2 cursor-pointer text-[10px] font-bold text-slate-700 dark:text-zinc-300">
                    <input
                      type="checkbox"
                      checked={fisicoSellosCoinciden}
                      onChange={(e) => setFisicoSellosCoinciden(e.target.checked)}
                      className="rounded text-purple-600"
                    />
                    <span>Sellos Coinciden 100%</span>
                  </label>

                  <label className="flex items-center gap-2 cursor-pointer text-[10px] font-bold text-slate-700 dark:text-zinc-300">
                    <input
                      type="checkbox"
                      checked={fisicoEtiquetadoNOM}
                      onChange={(e) => setFisicoEtiquetadoNOM(e.target.checked)}
                      className="rounded text-purple-600"
                    />
                    <span>Cumple Etiquetado NOMs</span>
                  </label>
                </div>
              </div>
            </div>

            {/* Botones de Práctica para Alumnos / Docentes */}
            <div className="flex items-center justify-between pt-2">
              <button
                type="button"
                onClick={handleInyectarDiscrepancia}
                className="px-3 py-1.5 rounded-xl border border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-400 text-xs font-bold hover:bg-amber-500/20 transition-all flex items-center gap-1.5"
              >
                <AlertTriangle className="h-3.5 w-3.5" />
                <span>Simular Discrepancia Física</span>
              </button>

              <button
                type="button"
                onClick={handleRestablecerConforme}
                className="px-3 py-1.5 rounded-xl border border-slate-200 dark:border-white/10 text-slate-600 dark:text-zinc-400 text-xs font-bold hover:bg-slate-100 dark:hover:bg-white/5 transition-all flex items-center gap-1.5"
              >
                <RefreshCw className="h-3.5 w-3.5" />
                <span>Restablecer Igualdad</span>
              </button>
            </div>

            {/* Observaciones del Previo */}
            <div>
              <label className="block text-[10px] font-bold text-slate-600 dark:text-zinc-400 uppercase mb-1">
                Dictamen u Observaciones del Previo Ocular
              </label>
              <textarea
                rows={2}
                value={observacionPrevio}
                onChange={(e) => setObservacionPrevio(e.target.value)}
                placeholder="Ej. Mercancía cotejada en patio fiscal sin discrepancias en marcas ni sellos. Etiquetado comercial conforme a NOM-050-SCFI."
                className="w-full px-3 py-2 rounded-xl text-xs bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 dark:text-white"
              />
            </div>

            {/* Acciones de Cierre del Previo */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-3 border-t border-slate-100 dark:border-white/5">
              {!sonIguales && (
                <button
                  type="button"
                  onClick={handleLevantarDiscrepancia}
                  className="w-full sm:w-auto px-4 py-2.5 rounded-xl border border-rose-500 bg-rose-500/10 text-rose-600 dark:text-rose-400 font-bold text-xs uppercase tracking-wider hover:bg-rose-500/20 transition-all flex items-center justify-center gap-2"
                >
                  <AlertTriangle className="h-4 w-4" />
                  <span>Levantar Acta de Discrepancia (Evitar PAMA)</span>
                </button>
              )}

              <button
                type="button"
                onClick={handleAprobarPrevio}
                disabled={!sonIguales}
                className="w-full sm:w-auto px-5 py-2.5 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-black text-xs uppercase tracking-wider shadow-lg shadow-emerald-500/25 hover:scale-105 active:scale-95 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:pointer-events-none"
              >
                <CheckCircle2 className="h-4 w-4" />
                <span>Previo Conforme ➔ Pasar a Pedimento</span>
              </button>
            </div>
          </div>
        </div>

        {/* ========================================================================= */}
        {/* COLUMNA 3 (3 COLS): PEDIMENTO, FRACCIÓN, RRNAS Y CÁLCULO IGI */}
        {/* ========================================================================= */}
        <div className="lg:col-span-3 space-y-5">
          <div className="p-5 rounded-2xl border border-slate-200/80 bg-white dark:border-white/10 dark:bg-[#0c0c0e] shadow-sm space-y-4">
            <div className="flex items-center justify-between pb-3 border-b border-slate-100 dark:border-white/5">
              <h3 className="text-xs font-black uppercase tracking-wider text-slate-900 dark:text-white flex items-center gap-2">
                <Calculator className="h-4 w-4 text-emerald-500" />
                4. Pedimento, Fracción & IGI
              </h3>
              <span className="font-mono text-[9px] text-slate-400">TIGIE</span>
            </div>

            {/* Fracción Arancelaria y NICO */}
            <div>
              <label className="block text-[10px] font-bold text-slate-600 dark:text-zinc-400 uppercase mb-1">
                Fracción Arancelaria (TIGIE / NICO)
              </label>
              <input
                type="text"
                value={fraccionArancelaria}
                onChange={(e) => setFraccionArancelaria(e.target.value)}
                className="w-full px-3 py-1.5 rounded-lg text-xs font-mono font-bold bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 dark:text-white"
              />
              <span className="text-[9px] text-slate-500 mt-0.5 block">
                Capítulo.Partida.Subpartida.Fracción.NICO
              </span>
            </div>

            {/* Selector de los 3 Tipos de Arancel del Pizarrón */}
            <div className="space-y-2">
              <label className="block text-[10px] font-bold text-slate-600 dark:text-zinc-400 uppercase">
                Tipo de Arancel IGI (LIGIE):
              </label>
              <div className="grid grid-cols-3 gap-1 text-[10px] font-bold">
                <button
                  type="button"
                  onClick={() => setTipoArancel('AD_VALOREM')}
                  className={`py-1.5 px-2 rounded-lg border text-center transition-all ${
                    tipoArancel === 'AD_VALOREM'
                      ? 'border-emerald-500 bg-emerald-500/10 text-emerald-400'
                      : 'border-slate-200 dark:border-white/10 text-slate-600 dark:text-zinc-400'
                  }`}
                >
                  Ad-valorem %
                </button>

                <button
                  type="button"
                  onClick={() => setTipoArancel('ESPECIFICO')}
                  className={`py-1.5 px-2 rounded-lg border text-center transition-all ${
                    tipoArancel === 'ESPECIFICO'
                      ? 'border-emerald-500 bg-emerald-500/10 text-emerald-400'
                      : 'border-slate-200 dark:border-white/10 text-slate-600 dark:text-zinc-400'
                  }`}
                >
                  Específico $
                </button>

                <button
                  type="button"
                  onClick={() => setTipoArancel('MIXTO')}
                  className={`py-1.5 px-2 rounded-lg border text-center transition-all ${
                    tipoArancel === 'MIXTO'
                      ? 'border-emerald-500 bg-emerald-500/10 text-emerald-400'
                      : 'border-slate-200 dark:border-white/10 text-slate-600 dark:text-zinc-400'
                  }`}
                >
                  Mixto (% + $)
                </button>
              </div>
            </div>

            {/* Parámetros de la Tasa */}
            {(tipoArancel === 'AD_VALOREM' || tipoArancel === 'MIXTO') && (
              <div>
                <label className="block text-[9px] font-bold text-slate-500 uppercase">
                  Tasa Ad-Valorem (% sobre Valor Aduana)
                </label>
                <div className="flex items-center gap-2 mt-0.5">
                  <input
                    type="number"
                    value={tasaAdValorem}
                    onChange={(e) => setTasaAdValorem(Number(e.target.value))}
                    className="w-full px-2.5 py-1.5 rounded-lg text-xs font-mono font-bold bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 dark:text-white"
                  />
                  <span className="text-xs font-bold text-slate-500">%</span>
                </div>
              </div>
            )}

            {(tipoArancel === 'ESPECIFICO' || tipoArancel === 'MIXTO') && (
              <div>
                <label className="block text-[9px] font-bold text-slate-500 uppercase">
                  Cuota Específica ($ USD / 100 kg)
                </label>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-xs font-bold text-slate-500">$</span>
                  <input
                    type="number"
                    step="0.01"
                    value={cuotaEspecificaUSD}
                    onChange={(e) => setCuotaEspecificaUSD(Number(e.target.value))}
                    className="w-full px-2.5 py-1.5 rounded-lg text-xs font-mono font-bold bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 dark:text-white"
                  />
                  <span className="text-xs font-bold text-slate-500">USD</span>
                </div>
              </div>
            )}

            {/* Checklist RRNAs (Pizarrón) */}
            <div className="p-3 rounded-xl bg-slate-50 dark:bg-white/[0.02] border border-slate-200 dark:border-white/10 space-y-1.5">
              <span className="text-[10px] font-black uppercase text-purple-600 dark:text-purple-400 block">
                RRNA's Aplicables (Permisos Oficiales):
              </span>
              <label className="flex items-center gap-2 cursor-pointer text-[10px] text-slate-700 dark:text-zinc-300">
                <input
                  type="checkbox"
                  checked={cumpleRRNAs}
                  onChange={(e) => setCumpleRRNAs(e.target.checked)}
                  className="rounded text-purple-600"
                />
                <span className="font-bold">Permiso SE / Certificado Fitosanitario OK</span>
              </label>
            </div>

            {/* Liquidación de Gravámenes */}
            <div className="p-3 rounded-xl bg-slate-900 text-white font-mono text-[10px] space-y-1.5">
              <div className="flex justify-between text-slate-400">
                <span>VALOR ADUANA:</span>
                <span className="text-white">${calculosIGI.valorAduanaUSD.toLocaleString()} USD</span>
              </div>
              <div className="flex justify-between text-slate-400">
                <span>IGI CALCULADO:</span>
                <span className="text-amber-400 font-bold">${calculosIGI.igiUSD.toLocaleString()} USD</span>
              </div>
              <div className="flex justify-between text-slate-400">
                <span>DTA + PRV + IVA:</span>
                <span className="text-white">${(calculosIGI.dtaUSD + calculosIGI.prvUSD + calculosIGI.ivaUSD).toLocaleString()} USD</span>
              </div>
              <div className="border-t border-slate-700 pt-1.5 flex justify-between font-bold text-xs text-emerald-400">
                <span>TOTAL PEDIMENTO:</span>
                <span>${calculosIGI.totalUSD.toLocaleString()} USD</span>
              </div>
            </div>

            {/* Botón Abrir Hoja Física Oficial del Pedimento */}
            <button
              type="button"
              onClick={() => onAbrirHojaFisica('PEDIMENTO')}
              className="w-full py-2.5 rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 text-slate-950 font-black text-xs uppercase tracking-wider shadow-md shadow-amber-500/20 hover:scale-105 active:scale-95 transition-all flex items-center justify-center gap-2"
            >
              <FileCheck className="h-4 w-4" />
              <span>Ver Pedimento SAT en Hoja Física</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
